import * as React from 'react';
import { DayOfWeek, IOffice } from 'models/shop/office';
import { lowerFirstObjectPropertiesNames } from 'helpers/common';
import { ICustomer } from 'models/shop/customer';
import { IPage } from 'models/common';
import { momentWithTimezone } from 'redux/modules/shop/helpers';
import { IServiceGroup } from 'models/shop/serviceGroups';
import { IServicesList } from 'models/shop/shopBookingTemplates';
import { IScheduleItem } from 'models/shop/schedule';

export const renderOfficeOpeningTimeReadOnly = (
	day: DayOfWeek,
	t: (key: string, obj?: any) => string,
	office: IOffice,
) => {
	const valueIndex = (office?.officeTimes || []).map(lowerFirstObjectPropertiesNames).findIndex((ot) => ot.day === day);
	const value = valueIndex < 0 ? undefined : office?.officeTimes[valueIndex];
	let times: JSX.Element | string = (
		<span className="muted">{t('Shop_ManageShop_Location_Label_OpeningTimes_Closed')}</span>
	);
	const timezone = office?.zoneInfo;

	if (value) {
		times =
			momentWithTimezone(timezone, value.openTime).format('HH:mm') +
			' - ' +
			momentWithTimezone(timezone, value.closeTime).format('HH:mm');
	}
	return (
		<React.Fragment key={day}>
			<dt>{t('Day_' + day)}</dt>
			<dd>{times}</dd>
		</React.Fragment>
	);
};

export const renderOpeningTimeReadOnly = (
	officeIndex: string,
	day: DayOfWeek,
	t: (key: string, obj?: any) => string,
	currentFormValues: IOffice[],
) => {
	const index = Number(officeIndex);
	if (isNaN(index) || index < 0) {
		return null;
	}
	// Check for when data is loaded after mount
	if (!currentFormValues) {
		return null;
	}
	const office = currentFormValues[index];
	return renderOfficeOpeningTimeReadOnly(day, t, office);
};

export const currentOffice = (offices: IOffice[], officeId: string, shopId: string): IOffice => {
	// TODO remove x.shopId === shopCardData.shopcard.id once links are of format /shop/code/officeID
	if (offices && officeId && shopId) {
		const result = offices.filter((x) => (officeId ? x.id === officeId : x.shopId === shopId));
		return result[0];
	}
};

export const formattedPrice = (value: number): string => {
	return new Intl.NumberFormat('en', { minimumFractionDigits: 2 }).format(value);
};

export const selectedCustomer = (customers: IPage<ICustomer>, customerId: string): ICustomer => {
	if (customers && customers.items?.length && customerId) {
		return customers.items.find((x) => x.id === customerId);
	}
};

export const groupBy: <T, K extends keyof T>(values: T[], key: K) => { [key: string]: T[] } = (
	values,
	key,
	nullKeyword = 'null',
) => {
	return values.reduce((prev, curr: any) => {
		const newKey = curr[key];
		const nonullkey = newKey == null ? nullKeyword : newKey;
		(prev[nonullkey] = prev[nonullkey] || []).push(curr);
		return prev;
	}, {} as any);
};

export const groupByUnique: <T, K extends keyof T>(values: T[], key: K) => { [key: string]: T } = (
	values,
	key,
	nullKeyword = 'null',
) => {
	return values?.reduce((prev, curr: any) => {
		const newKey = curr[key];
		const nonullkey = newKey == null ? nullKeyword : newKey;
		prev[nonullkey] = curr;
		return prev;
	}, {} as any);
};

/**
 * Groups values by the first key then groups the inner arrays by the second key and so forth.
 * Output type is { [key: string]: { [key: string]: ...N... { [key: string]:  IServicesList[] } ...N.. } }
 * where N is the length of keys.
 */
export const groupByMultiple: <T, K extends keyof T>(
	values: T[],
	keys: K[],
	nullKeyword?: string,
) => { [key: string]: any } = (values, keys, nullKeyword = 'null') => {
	const out = {};
	values.forEach((value) => {
		let curr = out as any;
		for (let i = 0; i < keys.length; i++) {
			const newKey = keys[i];
			const nonullkey = value[newKey] == null ? nullKeyword : value[newKey];

			if (keys.length - 1 == i) {
				(curr[nonullkey] = curr[nonullkey] || []).push(value);
			} else {
				curr[nonullkey] = curr[nonullkey] || {};
				curr = curr[nonullkey];
			}
		}
	});
	return out;
};

export interface IGroupedServices {
	/**
	 * Sorted category keys
	 */
	order: string[];
	/**
	 * Service groups by category key
	 */
	children: {
		[key: string]: {
			// Ordered service group ids
			order: string[];
			children: {
				[key: string]: {
					/**
					 * Service group
					 */
					serviceGroup: IServiceGroup;
					/**
					 * Sorted service ids
					 */
					order: string[];
					/**
					 * Services by service Id
					 */
					children: {
						[key: string]: {
							/**
							 * Service
							 */
							serviceGroup: IServiceGroup;
							/**
							 * ordered variant parent Ids (subservice ids)
							 */
							order: string[];

							/**
							 * Services by variant parent id, by id
							 */
							children: {
								[key: string]: IServicesList[];
							};
						};
					};
				};
			};
		};
	};
}

export const groupServices = (
	serviceGroups: IServiceGroup[],
	services: IServicesList[],
	calculateOrder?: boolean,
): IGroupedServices => {
	const serviceGroupsByParentId = groupBy(serviceGroups, 'parentId');
	const serviceGroupsByCategoryIdById: { [key: string]: { [key: string]: IServicesList[] } } = groupByMultiple(
		serviceGroups,
		['serviceCategoryId', 'id'],
	);

	const servicesBySgByVp: { [key: string]: { [key: string]: IServicesList[] } } = groupByMultiple(services, [
		'serviceGroupId',
		'variantParentId',
	]);
	const outObj = {
		order: Object.keys(serviceGroupsByCategoryIdById).sort((a, b) => {
			for (const sg1 in serviceGroupsByCategoryIdById[a]) {
				for (const sg2 in serviceGroupsByCategoryIdById[b]) {
					return serviceGroupsByCategoryIdById[a][sg1][0].order - serviceGroupsByCategoryIdById[b][sg2][0].order;
				}
			}
		}),
		children: Object.keys(serviceGroupsByCategoryIdById).reduce((out, categoryId) => {
			const serviceCategoriesOfCategory = serviceGroupsByParentId['null'].filter(
				(sg) => String(sg.serviceCategoryId) == String(categoryId),
			);
			out[categoryId] = {
				order: serviceCategoriesOfCategory?.sort((a, b) => a.order - b.order)?.map((x) => x.id),
				children: serviceCategoriesOfCategory.reduce((categoryObj, parentServiceGroup) => {
					categoryObj[parentServiceGroup.id] = {
						serviceGroup: parentServiceGroup,
						order: calculateOrder
							? serviceGroupsByParentId[parentServiceGroup.id]?.sort((a, b) => a.order - b.order)?.map((x) => x.id)
							: undefined,
						children: serviceGroupsByParentId[parentServiceGroup.id]?.reduce((serviceGroupObj, serviceGroup) => {
							serviceGroupObj[serviceGroup.id] = {
								serviceGroup: serviceGroup,
								order: calculateOrder
									? servicesBySgByVp?.[serviceGroup?.id] &&
									  Object.keys(servicesBySgByVp[serviceGroup.id]).sort(
											(a, b) =>
												servicesBySgByVp[serviceGroup.id][a][0].order - servicesBySgByVp[serviceGroup.id][b][0].order,
									  )
									: undefined,
								children: servicesBySgByVp[serviceGroup.id],
							};
							return serviceGroupObj;
						}, {}),
					};

					return categoryObj;
				}, {}),
			};
			return out;
		}, {}),
	};

	return outObj;
};

export const unGroup = (baseElement, key, values) => values.map((val) => ({ ...baseElement, [key]: val }));

/**
 * Generate <lenth of valuesObj> with data from baseElement and keys copied from valuesObj
 */
export const unGroupCopy = (baseElement, sourcekeyNames: string[], valuesObj: any[], keyNames?: string[]) =>
	valuesObj.map((valObj) => {
		const copy = { ...baseElement };
		sourcekeyNames.forEach((key, i) => (copy[keyNames?.[i] || key] = valObj[key]));
		return copy;
	});

export const doScheduleItemsOverlap = (item1: IScheduleItem, item2: IScheduleItem, ignoreColumn): boolean => {
	// explanation https://stackoverflow.com/questions/325933/determine-whether-two-date-ranges-overlap/325964#325964
	if (!ignoreColumn && item1.column != item2.column) {
		return false;
	}
	return item1.start < item2.end && item1.end > item2.start;
};

// This is the graph colouring problem
// The optimal solution is O(n*2^n) which is not aplicable
// Therefore this is basically an implementation of the greedy algorithm
export const assignSubColumnsToItems = (
	items: IScheduleItem[],
	ignoreColumn = false,
	filterType: IScheduleItem['type'] = 'event',
): IScheduleItem[] => {
	let otherItems = [];
	if (filterType) {
		otherItems = [...items.filter((x) => x.type != filterType)];
		items = [...items.filter((x) => x.type == filterType)];
	} else {
		items = [...items];
	}

	const checkingOrder = items.map((_, i) => i);
	checkingOrder?.sort((a, b) =>
		items[b]?.highlightGroupId && items[a]?.highlightGroupId
			? items[a].highlightGroupId?.localeCompare(items[b].highlightGroupId)
			: 1,
	);

	// This is a map of the neighbours in this graph
	// i-th item overlaps with all items in the overlaps[i] array
	const overlaps = {};
	for (let orderI = 0; orderI < checkingOrder.length; orderI++) {
		const i = checkingOrder[orderI];
		for (let orderJ = orderI + 1; orderJ < checkingOrder.length; orderJ++) {
			const j = checkingOrder[orderJ];
			if (doScheduleItemsOverlap(items[i], items[j], ignoreColumn)) {
				(overlaps[i] = overlaps[i] || []).push(j);
				(overlaps[j] = overlaps[j] || []).push(i);
			}
		}

		const overlappingColumns = new Set();
		for (let j = 0; j < overlaps?.[i]?.length; j++) {
			const subColumn = items[overlaps[i][j]].subColumn;
			if (subColumn != null) {
				overlappingColumns.add(subColumn);
			}
		}

		let subColumn = 0;
		while (overlappingColumns.has(subColumn)) {
			subColumn++;
		}

		items[i] = Object.assign({}, items[i], { subColumn, subColSpan: 1 });
	}
	const connectedComponents = [];
	const checkedItems = new Set();

	/**
	 * Gets all nodes that are connected with a path to the source node. Performs BFS from the source node.
	 * @param i source node
	 * @returns list of nodes
	 */
	const getConnectedComponent = (i) => {
		const component = [];
		const queue = [i];
		if (checkedItems.has(i)) {
			return null;
		}
		checkedItems.add(i);
		while (queue.length) {
			const curr = queue.shift();
			component.push(curr);
			if (overlaps?.[curr]) {
				for (const overlap of overlaps[curr]) {
					if (!checkedItems.has(overlap)) {
						checkedItems.add(overlap);
						queue.push(overlap);
					}
				}
			}
		}
		return component;
	};

	for (let i = 0; i < items.length; i++) {
		if (checkedItems.has(i)) {
			continue;
		}
		connectedComponents.push(getConnectedComponent(i));
	}

	for (const comp of connectedComponents) {
		const subColumnCount =
			comp.reduce(
				(prev, curr) => (items[curr]?.subColumn && prev < items[curr].subColumn ? items[curr].subColumn : prev),
				0,
			) + 1;
		comp.forEach((i) => {
			// if it does not have a subColumn, it was not copied. Changing it makes an infinite loop.
			if (items[i].subColumn != null) items[i].totalSubColumns = subColumnCount;
		});
	}
	return items.concat(otherItems);
};
